// Self-Hosted Open Data owner release gate for the customer portal.
//
// Programme products (for example VISINVOKE Books Explorer) are shown as Coming Soon until the backend
// release overlay (/v1/open-data/release-state, derived from the owner's release registry) reports that a
// surface is open. Any failure, missing product or unexpected payload keeps the product Coming Soon, and
// public pages stay non-indexable. Releasing a product needs only the backend release change: the portal
// is not rebuilt.
import { useEffect, useState } from 'react';
import API from '../api';

export const OPEN_DATA_SURFACES = Object.freeze(['dcc', 'dashboard', 'public_product_page', 'seo_entity_pages', 'android']);

let cached = null;
let inflight = null;

export function parseReleaseState(payload) {
  const products = payload && typeof payload === 'object' && payload.products && typeof payload.products === 'object' ? payload.products : {};
  const out = {};
  Object.keys(products).forEach((slug) => {
    const entry = products[slug];
    if (!entry || typeof entry !== 'object') return;
    const surfaces = {};
    OPEN_DATA_SURFACES.forEach((name) => { surfaces[name] = entry.release_status === 'LIVE' && !!(entry.surfaces && entry.surfaces[name] === true); });
    out[slug] = { releaseStatus: entry.release_status === 'LIVE' ? 'LIVE' : (entry.release_status === 'RELEASE_AUTHORISED' ? 'RELEASE_AUTHORISED' : 'COMING_SOON'), surfaces };
  });
  return out;
}

export function surfaceOpen(state, slug, surface) {
  const entry = state && state[slug];
  return !!(entry && entry.releaseStatus === 'LIVE' && entry.surfaces && entry.surfaces[surface] === true);
}

export function openDataStatusLabel(state, slug, surface = 'dashboard') {
  return surfaceOpen(state, slug, surface) ? 'Available' : 'Coming Soon';
}

export function fetchOpenDataReleaseState() {
  if (cached) return Promise.resolve(cached);
  if (!inflight) {
    inflight = API.get('/v1/open-data/release-state')
      .then((response) => { cached = parseReleaseState(response && response.data); return cached; })
      .catch(() => ({}))
      .finally(() => { inflight = null; });
  }
  return inflight;
}

export function useOpenDataReleaseState(enabled = true) {
  const [state, setState] = useState(cached || {});
  useEffect(() => {
    if (!enabled) return undefined;
    let alive = true;
    fetchOpenDataReleaseState().then((value) => { if (alive) setState(value || {}); });
    return () => { alive = false; };
  }, [enabled]);
  return state;
}

export function resetOpenDataReleaseCacheForTests() {
  cached = null;
  inflight = null;
}
